/**
 * Field Registry Admin Page — Stage 9.3
 * Управление реестром полей проекта
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fieldRegistryAPI } from '@/api/fieldRegistry';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Database,
  Plus,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  Search,
  Lock,
  Eye,
  Calculator,
  FileSpreadsheet,
} from 'lucide-react';

// Категории полей
const CATEGORIES = [
  { value: 'general', label: 'Общая информация', icon: '📋' },
  { value: 'project_docs', label: 'Проработка (КП, GPL)', icon: '📄' },
  { value: 'dates', label: 'Сроки', icon: '📅' },
  { value: 'budget', label: 'Бюджет', icon: '💰' },
  { value: 'costs', label: 'Себестоимость', icon: '📊' },
  { value: 'margin', label: 'Маржа и прибыль', icon: '📈' },
  { value: 'vendor', label: 'Вендоры', icon: '🏭' },
  { value: 'bitrix', label: 'Bitrix24', icon: '🔗' },
  { value: 'status', label: 'Статус проекта', icon: '🚦' },
  { value: 'audit', label: 'Аудит', icon: '🔍' },
];

// Типы данных
const DATA_TYPES = [
  { value: 'string', label: 'Текст' },
  { value: 'integer', label: 'Целое число' },
  { value: 'float', label: 'Число' },
  { value: 'currency', label: 'Валюта' },
  { value: 'percent', label: 'Процент' },
  { value: 'date', label: 'Дата' },
  { value: 'datetime', label: 'Дата и время' },
  { value: 'boolean', label: 'Да/Нет' },
  { value: 'enum', label: 'Список значений' },
  { value: 'reference', label: 'Ссылка' },
];

// Источники данных
const SOURCES = [
  { value: 'input', label: 'Ввод пользователя', color: 'bg-blue-100 text-blue-800' },
  { value: 'calculated', label: 'Расчётное', color: 'bg-purple-100 text-purple-800' },
  { value: 'bitrix', label: 'Из Bitrix24', color: 'bg-orange-100 text-orange-800' },
  { value: 'system', label: 'Системное', color: 'bg-gray-100 text-gray-800' },
];

// Роли
const ROLES = [
  { value: 'finance_admin', label: 'Финансовый админ' },
  { value: 'senior_manager', label: 'Старший менеджер' },
  { value: 'viewer', label: 'Просмотр' },
  { value: 'all', label: 'Все' },
];

const FieldRegistryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [fields, setFields] = useState([]);
  const [categories, setCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [error, setError] = useState('');

  // Проверка прав
  const userRole = user?.role?.toLowerCase();
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      setLoading(true);
      const data = await fieldRegistryAPI.getAll({ active_only: false });
      setFields(data.fields || []);
      setCategories(data.categories || {});
    } catch (err) {
      showError(err, 'Ошибка загрузки реестра полей');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Заполнить реестр начальными данными? Существующие поля не будут перезаписаны.')) {
      return;
    }

    try {
      setSeeding(true);
      const result = await fieldRegistryAPI.seed();
      showSuccess(`Добавлено ${result.inserted} полей, пропущено ${result.skipped}`);
      await loadFields();
    } catch (err) {
      showError(err, 'Ошибка заполнения реестра');
    } finally {
      setSeeding(false);
    }
  };

  const handleEdit = (field) => {
    setSelectedField(field);
    setError('');
    setShowEditDialog(true);
  };

  const handleSaveField = async () => {
    if (!selectedField) return;

    try {
      await fieldRegistryAPI.update(selectedField.field_key, {
        label_ru: selectedField.label_ru,
        tooltip_ru: selectedField.tooltip_ru,
        required: selectedField.required,
        editable_roles: selectedField.editable_roles,
        visible_roles: selectedField.visible_roles,
        sort_order: selectedField.sort_order,
      });
      showSuccess('Поле обновлено');
      setShowEditDialog(false);
      await loadFields();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ошибка при сохранении';
      setError(errorMsg);
    }
  };

  const handleDelete = async (fieldKey) => {
    if (!window.confirm(`Деактивировать поле "${fieldKey}"?`)) return;

    try {
      await fieldRegistryAPI.delete(fieldKey);
      showSuccess('Поле деактивировано');
      await loadFields();
    } catch (err) {
      showError(err, 'Ошибка при удалении');
    }
  };

  const getSourceBadge = (source) => {
    const s = SOURCES.find((x) => x.value === source);
    return s || SOURCES[0];
  };

  const getCategoryLabel = (category) => {
    const c = CATEGORIES.find((x) => x.value === category);
    return c ? `${c.icon} ${c.label}` : category;
  };

  // Фильтрация полей
  const filteredFields = fields.filter((f) => {
    const matchesCategory = activeCategory === 'all' || f.category === activeCategory;
    const matchesSearch =
      !searchQuery ||
      f.field_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.label_ru.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка реестра полей...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="field-registry-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Database className="w-6 h-6" />
                  Реестр полей проекта
                </h1>
                <p className="text-sm text-gray-600">
                  {fields.length} полей в {Object.keys(categories).length} категориях
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSeed}
                    disabled={seeding}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {seeding ? 'Загрузка...' : 'Загрузить шаблон'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadFields}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Обновить
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mt-4 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Поиск по ключу или названию..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="flex flex-wrap gap-1 mb-6 bg-transparent h-auto">
            <TabsTrigger value="all" className="data-[state=active]:bg-indigo-100">
              Все ({fields.length})
            </TabsTrigger>
            {CATEGORIES.map((cat) => {
              const count = (categories[cat.value] || []).length;
              if (count === 0) return null;
              return (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  className="data-[state=active]:bg-indigo-100"
                >
                  {cat.icon} {cat.label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {activeCategory === 'all' ? 'Все поля' : getCategoryLabel(activeCategory)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-3 font-semibold">Ключ</th>
                      <th className="text-left py-3 px-3 font-semibold">Название (RU)</th>
                      <th className="text-left py-3 px-3 font-semibold">Тип</th>
                      <th className="text-left py-3 px-3 font-semibold">Источник</th>
                      <th className="text-center py-3 px-3 font-semibold">
                        <Lock className="w-4 h-4 inline" title="Редактирование" />
                      </th>
                      <th className="text-center py-3 px-3 font-semibold">
                        <Eye className="w-4 h-4 inline" title="Просмотр" />
                      </th>
                      <th className="text-center py-3 px-3 font-semibold">
                        <Calculator className="w-4 h-4 inline" title="Формула" />
                      </th>
                      {isAdmin && <th className="text-right py-3 px-3 font-semibold">Действия</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFields.map((field) => {
                      const sourceBadge = getSourceBadge(field.source);
                      const isInactive = !field.is_active;

                      return (
                        <tr
                          key={field.field_key}
                          className={`border-b hover:bg-gray-50 ${isInactive ? 'opacity-50' : ''}`}
                        >
                          <td className="py-3 px-3">
                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {field.field_key}
                            </code>
                            {field.required && (
                              <span className="text-red-500 ml-1" title="Обязательное">
                                *
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 font-medium">{field.label_ru}</td>
                          <td className="py-3 px-3 text-gray-600">
                            {DATA_TYPES.find((t) => t.value === field.data_type)?.label ||
                              field.data_type}
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={sourceBadge.color}>{sourceBadge.label}</Badge>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-xs text-gray-500">
                              {field.editable_roles?.length > 0
                                ? field.editable_roles.join(', ')
                                : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-xs text-gray-500">
                              {field.visible_roles?.includes('all')
                                ? 'все'
                                : field.visible_roles?.join(', ') || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            {field.calc_formula ? (
                              <span
                                className="text-xs text-purple-600 cursor-help"
                                title={field.calc_formula}
                              >
                                ✓
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          {isAdmin && (
                            <td className="py-3 px-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(field)}
                                  title="Редактировать"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(field.field_key)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Деактивировать"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredFields.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {fields.length === 0 ? (
                      <div>
                        <p className="mb-4">Реестр полей пуст</p>
                        {isAdmin && (
                          <Button onClick={handleSeed} disabled={seeding}>
                            <Download className="w-4 h-4 mr-2" />
                            Загрузить шаблон Кати
                          </Button>
                        )}
                      </div>
                    ) : (
                      'Поля не найдены'
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info card */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">Реестр полей — Stage 9</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>
                      <strong>input</strong> — поля для ввода пользователем
                    </li>
                    <li>
                      <strong>calculated</strong> — автоматически рассчитываемые поля
                    </li>
                    <li>
                      <strong>bitrix</strong> — синхронизируются из Bitrix24
                    </li>
                    <li>
                      <strong>system</strong> — системные поля (created_at, updated_at)
                    </li>
                  </ul>
                  <p className="mt-2">
                    Права на редактирование и просмотр полей определяют, какие пользователи могут
                    видеть и изменять данные в формах проекта.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Tabs>
      </main>

      {/* Edit Field Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Редактирование поля</DialogTitle>
            <DialogDescription>
              <code className="bg-gray-100 px-2 py-1 rounded">{selectedField?.field_key}</code>
            </DialogDescription>
          </DialogHeader>

          {selectedField && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Название (RU)</Label>
                <Input
                  value={selectedField.label_ru}
                  onChange={(e) =>
                    setSelectedField({ ...selectedField, label_ru: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Подсказка (tooltip)</Label>
                <Input
                  value={selectedField.tooltip_ru || ''}
                  onChange={(e) =>
                    setSelectedField({ ...selectedField, tooltip_ru: e.target.value })
                  }
                  placeholder="Текст подсказки при наведении"
                />
              </div>

              <div className="space-y-2">
                <Label>Порядок сортировки</Label>
                <Input
                  type="number"
                  value={selectedField.sort_order}
                  onChange={(e) =>
                    setSelectedField({ ...selectedField, sort_order: parseInt(e.target.value) || 0 })
                  }
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={selectedField.required}
                  onChange={(e) =>
                    setSelectedField({ ...selectedField, required: e.target.checked })
                  }
                />
                <Label htmlFor="required">Обязательное поле</Label>
              </div>

              <div className="space-y-2">
                <Label>Кто может редактировать</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.filter((r) => r.value !== 'all').map((role) => (
                    <label key={role.value} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedField.editable_roles?.includes(role.value)}
                        onChange={(e) => {
                          const roles = selectedField.editable_roles || [];
                          if (e.target.checked) {
                            setSelectedField({
                              ...selectedField,
                              editable_roles: [...roles, role.value],
                            });
                          } else {
                            setSelectedField({
                              ...selectedField,
                              editable_roles: roles.filter((r) => r !== role.value),
                            });
                          }
                        }}
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Кто может видеть</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((role) => (
                    <label key={role.value} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedField.visible_roles?.includes(role.value)}
                        onChange={(e) => {
                          const roles = selectedField.visible_roles || [];
                          if (e.target.checked) {
                            if (role.value === 'all') {
                              setSelectedField({ ...selectedField, visible_roles: ['all'] });
                            } else {
                              setSelectedField({
                                ...selectedField,
                                visible_roles: [...roles.filter((r) => r !== 'all'), role.value],
                              });
                            }
                          } else {
                            setSelectedField({
                              ...selectedField,
                              visible_roles: roles.filter((r) => r !== role.value),
                            });
                          }
                        }}
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
              </div>

              {selectedField.calc_formula && (
                <div className="space-y-2">
                  <Label>Формула расчёта</Label>
                  <code className="block bg-gray-100 p-2 rounded text-sm">
                    {selectedField.calc_formula}
                  </code>
                  <p className="text-xs text-gray-500">
                    Зависимости: {selectedField.calc_dependencies?.join(', ') || '—'}
                  </p>
                </div>
              )}

              {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Отмена
            </Button>
            <Button onClick={handleSaveField}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldRegistryPage;
